/**
 * Calendar validation for line dates (#1842).
 *
 * Mirrors backend/layers/utils/utils/dateValidator.js. The backend
 * is the gate; this exists so a typo is caught at the field instead of coming
 * back as a 400, and so the pickers can advertise the same bounds.
 *
 * The day check is integer arithmetic, never a Date round-trip:
 * `new Date('2026-02-31T00:00:00')` rolls forward to March 3 rather than
 * returning Invalid Date, so an impossible day survives a Date-based check.
 * That is exactly how 2026-02-31 passed the one guarded form before this.
 */

const DATE_STAMP_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

// Must match MIN_YEAR / MAX_YEAR in backend/layers/utils/utils/dateValidator.js.
export const MIN_YEAR = 1900;
export const MAX_YEAR = 2100;

// Bounds for the `min` and `max` attributes of a date input.
export const DATE_STAMP_MIN = '1900-01-01';
export const DATE_STAMP_MAX = '2100-12-31';

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYear(year) {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

function daysInMonth(year, month) {
  if (month === 2 && isLeapYear(year)) return 29;
  return DAYS_IN_MONTH[month - 1];
}

/**
 * Shape check only: four digits, two digits, two digits.
 *
 * @param {*} value
 * @returns {boolean}
 */
export function hasDateStampFormat(value) {
  return typeof value === 'string' && DATE_STAMP_REGEX.test(value);
}

/**
 * Full check: shape, year within bounds, month 1-12, and a day that exists in
 * that month of that year (leap years included).
 *
 * @param {*} value
 * @returns {boolean}
 */
export function isValidDateStamp(value) {
  if (typeof value !== 'string') return false;

  const match = DATE_STAMP_REGEX.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (year < MIN_YEAR || year > MAX_YEAR) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > daysInMonth(year, month)) return false;

  return true;
}

/**
 * Single message for every failure mode, so all four line save paths speak
 * with one voice.
 */
export const INVALID_DATE_MESSAGE = 'Enter a real date between 1900 and 2100.';
